import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { ReactNode } from 'react'
import type { AppRouteMatch } from './routeTypes'

import { selectLoggedIn } from 'selectors/accountSelectors'
import { isOidcSession } from 'agentHub/auth/oidc/authMethod'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { pushWithRegion } from 'actions/NavigationActions'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import { isCheckout, selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import { getCheckoutDepth } from 'checkout/hooks/useCheckoutRouteTracker'
import { setCheckoutGuestMode } from 'actions/CheckoutActions'
import useGuestCheckoutEligibility from 'hooks/useGuestCheckoutEligibility'
import { canRestoreGuestCheckout, getGuestCheckoutRollout, hasGuestCheckoutSession, hasGuestCheckoutSessionCartId, setGuestCheckoutSessionCartId } from 'lib/checkout/guestCheckout'

interface Props {
  children?: ReactNode
  match: AppRouteMatch | null
  isLoggedIn: boolean
  regionCode: string
  accountAccessModalDismissable?: boolean
  showPopup?: boolean
  loader?: React.ReactNode
  accountAccessModalInitialMode?: App.UiAccountModalMode
}

function AuthRouteComponent(props: Props) {
  const {
    isLoggedIn,
    regionCode,
    showPopup,
    children,
    loader,
    accountAccessModalDismissable,
    accountAccessModalInitialMode,
  } = props
  const { match } = props

  const dispatch = useAppDispatch()
  const path = match?.path
  const initComplete = useAppSelector(state => state.auth.initComplete)
  const isCheckoutPage = useAppSelector(isCheckout)
  const navigation = useAppNavigation()
  const showModal = useContext(ModalContext)

  // While an Agent Hub (OIDC) session is being restored after an SSR-anonymous
  // render, auth bootstrap hasn't settled (initComplete is false) and
  // useOidcSessionCoordinator is refreshing the token to log the user straight in.
  // Derived in render (not in the effect) so the effect depends on this boolean
  // rather than initComplete — otherwise init settling would re-run the effect and
  // re-open the modal for a genuinely logged-out user.
  const restoringOidcSession = !initComplete && isOidcSession()

  const isGuestCheckoutMode = useAppSelector(selectIsGuestCheckoutMode)
  const guestCheckoutCart = useAppSelector(state => state.checkout.cart)
  const isCartRestored = useAppSelector(state => state.checkout.isCartRestored)
  // A restored cart is checked against the rollout that admitted its traffic.
  const guestCheckoutRollout = useAppSelector(state => getGuestCheckoutRollout(state.utm.bp))
  const { isEligiblePurchase } = useGuestCheckoutEligibility(isLoggedIn, guestCheckoutRollout)
  const isCheckoutRoute = !!path?.startsWith('/:regionCode/checkout')
  const hasPendingGuestCheckoutSession = hasGuestCheckoutSession()
  const hasMatchingGuestCart = hasGuestCheckoutSessionCartId(guestCheckoutCart.cartId)
  const guestCheckoutPurchase = useMemo(() => ({
    // Guest mode is only created after the offer-page walled-garden check; checkout cart items do
    // not retain it.
    isWalledGarden: false,
    isMultiRoom: guestCheckoutCart.items.filter(item => item.itemType === 'hotel' || item.itemType === 'bedbankHotel').length > 1,
    itemTypes: guestCheckoutCart.items.map(item => item.itemType),
  }), [guestCheckoutCart.items])
  // The rollout controls entry to guest checkout, not restoration of an
  // already-admitted cart. Restoration still enforces the stable constraints.
  const isEligibleGuestPurchase = isEligiblePurchase(guestCheckoutPurchase)
  const allowGuestCheckout = isCheckoutRoute && canRestoreGuestCheckout({
    hasMatchingSessionCart: hasMatchingGuestCart,
    isCartRestored,
    isGuestCheckoutMode,
    isEligiblePurchase: isEligibleGuestPurchase,
  })
  const isGuestCheckoutRestorePending = !isLoggedIn &&
    isCheckoutRoute &&
    !isCartRestored &&
    (isGuestCheckoutMode || hasPendingGuestCheckoutSession)

  useEffect(() => {
    // Silent registration authenticates the customer without ending their
    // guest-checkout journey. Only invalid anonymous guest state is cleared.
    const shouldClearInvalidGuestState = isCheckoutRoute && isCartRestored && !isLoggedIn && !allowGuestCheckout

    if (!shouldClearInvalidGuestState) return

    if (isGuestCheckoutMode) {
      dispatch(setCheckoutGuestMode(false))
    } else {
      setGuestCheckoutSessionCartId(null)
    }
  }, [allowGuestCheckout, dispatch, isCartRestored, isCheckoutRoute, isGuestCheckoutMode, isLoggedIn])

  /**
   * CheckoutDepth is 0 if we're on the first checkout step.
   * Thus, we sum 1 to get the previous page, which is the offer page.
   */
  const goBackToOfferPage = useCallback(() => {
    const checkoutDepth = getCheckoutDepth()
    navigation.go(-(checkoutDepth + 1))
  }, [navigation])

  useEffect(() => {
    // Wait for OIDC or guest checkout restoration before prompting for login.
    if (
      showPopup &&
      !isLoggedIn &&
      !restoringOidcSession &&
      !allowGuestCheckout &&
      !isGuestCheckoutRestorePending
    ) {
      const useTripPlannerVersion = !!path?.startsWith('/:regionCode/trip-planner/trip/')
      showModal<AccountAccessModalResult>(<AccountAccessModal
        dismissable={accountAccessModalDismissable}
        initialMode={accountAccessModalInitialMode || (useTripPlannerVersion ? 'tripPlannerLogin' : 'login')}
      />).then(result => {
        if (result?.loggedIn) {
          // success, let them through
        } else {
          // no success, they closed it or something else
          const isRestoreCart = path?.includes('/checkout-restore-cart')
          if (isCheckoutPage && !isRestoreCart) {
            goBackToOfferPage()
          } else {
            dispatch(pushWithRegion('/'))
          }
        }
      })
    }

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [accountAccessModalDismissable, isLoggedIn, showPopup, path, restoringOidcSession, allowGuestCheckout, isGuestCheckoutRestorePending])

  if (isGuestCheckoutRestorePending) {
    return loader ?? <LoadingIndicator />
  }

  if (!isLoggedIn && allowGuestCheckout) {
    return <>{children}</>
  }

  if (!isLoggedIn && showPopup) {
    // We're waiting for the response on the auth modal now
    if (loader) {
      return loader
    }
    return <LoadingIndicator />
  }

  if (!isLoggedIn) {
    // Don't have access to this route and we don't want to show a login pop up
    // send them directly back to the home page
    return <RedirectWithStatus code={302} to={`/${regionCode.toLowerCase()}`} />
  }

  return <>{children}</>
}

function mapStateToProps(state: App.State) {
  return {
    isLoggedIn: selectLoggedIn(state),
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(AuthRouteComponent)
